(() => {
  class MoleculeHeader extends HTMLElement {
    connectedCallback() {
      this.drawerOpen = false;
      this.activeMega = null;
      this._megaCloseTimer = null;
      this._megaHideTimer = null;
      this._boundDocPointer = this.onDocumentPointer.bind(this);
      this._boundScroll = this.onScroll.bind(this);

      this.menuToggle = this.querySelector('[data-wm-menu-toggle]');
      this.drawer = this.querySelector('[data-wm-drawer]');
      this.bar = this.querySelector('.wm-bar');
      this.navbar = this.querySelector('[data-wm-navbar]');
      this.megaViewport = this.querySelector('[data-wm-mega-viewport]');
      this.overlay =
        this.parentElement?.querySelector('[data-wm-overlay]') ||
        document.querySelector('[data-wm-overlay]');
      this.sectionEl = this.closest('.shopify-section');
      this.headerGroupSections = () =>
        document.querySelectorAll('.shopify-section-group-header-group');

      this.syncStickySection();
      this.bindAll();
      this.syncDrawerOffset();
      this.syncHeaderGroupHeight();
      this.syncBarMetrics();
      this.onScroll();
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._boundScroll, { passive: true });
      document.addEventListener('pointermove', this._boundDocPointer, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._boundScroll);
      document.removeEventListener('pointermove', this._boundDocPointer);
      this.cancelCloseMega();
      if (this._megaHideTimer) window.clearTimeout(this._megaHideTimer);
      if (this._groupObserver) this._groupObserver.disconnect();
    }

    bindAll() {
      this._onResize = () => {
        this.syncBarMetrics();
        this.onScroll();
        this.syncDrawerOffset();
        this.syncHeaderGroupHeight();
        if (this.isDesktop()) {
          this.closeDrawer();
          if (this.activeMega) this.syncMegaViewportHeight();
        } else {
          this.closeMega();
        }
      };

      this.menuToggle?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDrawer();
      });

      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-wm-acc-toggle]');
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleAccordion(toggle);
        }
      });

      this.addEventListener(
        'pointerenter',
        (event) => {
          if (!this.isDesktop()) return;

          const megaTrigger = event.target.closest('[data-wm-mega-trigger]');
          if (megaTrigger && this.contains(megaTrigger)) {
            this.openMega(megaTrigger.getAttribute('data-wm-mega-trigger'), megaTrigger);
            return;
          }

          const plainTrigger = event.target.closest('.wm-nav__trigger');
          if (
            plainTrigger &&
            this.contains(plainTrigger) &&
            !plainTrigger.hasAttribute('data-wm-mega-trigger')
          ) {
            this.closeMega();
          }
        },
        true
      );

      this.addEventListener('focusin', (event) => {
        if (!this.isDesktop()) return;
        const plainTrigger = event.target.closest('.wm-nav__trigger');
        if (
          plainTrigger &&
          this.contains(plainTrigger) &&
          !plainTrigger.hasAttribute('data-wm-mega-trigger')
        ) {
          this.closeMega();
        }
      });

      this.querySelectorAll('[data-wm-mega-trigger]').forEach((trigger) => {
        trigger.addEventListener('focus', () => {
          if (this.isDesktop()) {
            this.openMega(trigger.getAttribute('data-wm-mega-trigger'), trigger);
          }
        });
      });

      this.overlay?.addEventListener('click', () => {
        this.closeDrawer();
        this.closeMega();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeDrawer();
          this.closeMega();
        }
      });
    }

    isDesktop() {
      return window.matchMedia('(min-width: 768px)').matches;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    syncStickySection() {
      if (!this.sectionEl) return;
      const sticky = this.getAttribute('data-sticky') === 'true';
      this.sectionEl.classList.toggle('wm-header-section--sticky', sticky);
    }

    syncBarMetrics() {
      if (!this.bar) return;
      const barH = Math.round(this.bar.offsetHeight);
      const navH = this.isDesktop() && this.navbar ? Math.round(this.navbar.offsetHeight) : 0;
      this.style.setProperty('--wm-bar-height', `${barH}px`);
      this._stickyStart = Math.max(
        0,
        Math.round(this.bar.getBoundingClientRect().top + window.scrollY)
      );
      this._combinedBarHeight = barH + navH;
    }

    onScroll() {
      if (!this.bar) return;
      const stickyEnabled = this.getAttribute('data-sticky') === 'true';
      const shouldStick =
        stickyEnabled && window.scrollY >= (this._stickyStart || 0);
      this.classList.toggle('is-bar-stuck', shouldStick);
      this.syncDrawerOffset();
    }

    syncHeaderGroupHeight() {
      const sections = this.headerGroupSections
        ? this.headerGroupSections()
        : document.querySelectorAll('.shopify-section-group-header-group');

      let height = 0;
      sections.forEach((el) => {
        height += el.offsetHeight;
      });

      if (!height) {
        const hero = document.querySelector('.wm-hero');
        if (hero) {
          height = Math.round(hero.getBoundingClientRect().top + window.scrollY);
        }
      }

      document.documentElement.style.setProperty(
        '--wm-header-group-height',
        `${Math.max(0, Math.round(height))}px`
      );
    }

    syncDrawerOffset() {
      const barBottom = this.bar ? Math.round(this.bar.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty('--wm-header-bar-bottom', `${barBottom}px`);
      this.style.setProperty('--wm-drawer-top', `${barBottom}px`);
    }

    onDocumentPointer(event) {
      if (!this.activeMega || !this.isDesktop()) return;
      if (this.isPointerInNavSystem(event.clientX, event.clientY)) {
        this.cancelCloseMega();
      } else {
        this.scheduleCloseMega();
      }
    }

    isPointerInNavSystem(x, y) {
      const hit = (el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      };

      // Keep open over the open mega panel only — not the full navbar
      // (empty space / clinic / pharmacy links should close it).
      if (this.megaViewport && this.classList.contains('is-mega-open') && hit(this.megaViewport)) {
        return true;
      }

      const openMega = this.querySelector('.wm-mega.is-open');
      if (openMega && hit(openMega)) return true;

      const triggers = this.querySelectorAll('[data-wm-mega-trigger]');
      for (const trigger of triggers) {
        if (hit(trigger)) return true;
        const item = trigger.closest('.wm-nav__item');
        if (item && hit(item)) return true;
      }

      return false;
    }

    scheduleCloseMega() {
      if (!this.isDesktop() || !this.activeMega) return;
      if (this._megaCloseTimer) return;
      this._megaCloseTimer = window.setTimeout(() => {
        this._megaCloseTimer = null;
        this.closeMega();
      }, 120);
    }

    cancelCloseMega() {
      if (this._megaCloseTimer) {
        window.clearTimeout(this._megaCloseTimer);
        this._megaCloseTimer = null;
      }
    }

    toggleDrawer() {
      if (this.drawerOpen) this.closeDrawer();
      else this.openDrawer();
    }

    openDrawer() {
      this.syncDrawerOffset();
      this.drawerOpen = true;
      this.classList.add('is-drawer-open');
      this.menuToggle?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('wm-nav-lock');
    }

    closeDrawer() {
      this.drawerOpen = false;
      this.classList.remove('is-drawer-open');
      this.menuToggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('wm-nav-lock');
      this.syncDrawerOffset();
      this.querySelectorAll('[data-wm-acc].is-open').forEach((acc) => {
        acc.classList.remove('is-open');
        const btn = acc.querySelector(':scope > [data-wm-acc-toggle]');
        const panel = acc.querySelector(':scope > .wm-acc__panel');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.setAttribute('aria-hidden', 'true');
      });
    }

    toggleAccordion(button) {
      const acc = button.closest('[data-wm-acc]');
      if (!acc) return;
      const willOpen = !acc.classList.contains('is-open');

      // Sibling L1 items collapse when another L1 opens (Figma single-expand feel at top level)
      if (willOpen && button.classList.contains('wm-acc__trigger--l1')) {
        const root = this.querySelector('.wm-drawer__nav');
        root?.querySelectorAll(':scope > [data-wm-acc].is-open').forEach((openAcc) => {
          if (openAcc === acc) return;
          this.setAccordion(openAcc, false);
        });
      }

      this.setAccordion(acc, willOpen);
    }

    setAccordion(acc, open) {
      const btn = acc.querySelector(':scope > [data-wm-acc-toggle]');
      const panel = acc.querySelector(':scope > .wm-acc__panel');
      acc.classList.toggle('is-open', open);
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) {
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }

      if (!open) {
        acc.querySelectorAll('[data-wm-acc].is-open').forEach((child) => {
          this.setAccordion(child, false);
        });
      }
    }

    getMegaTriggers() {
      return Array.from(this.querySelectorAll('[data-wm-mega-trigger]'));
    }

    getMegaIndex(id) {
      return this.getMegaTriggers().findIndex(
        (trigger) => trigger.getAttribute('data-wm-mega-trigger') === id
      );
    }

    syncMegaViewportHeight(target) {
      if (!this.megaViewport) return;
      const panel = target || this.querySelector('.wm-mega.is-open');
      if (!panel) {
        this.megaViewport.style.height = '0px';
        return;
      }
      this.megaViewport.style.height = `${Math.round(panel.offsetHeight)}px`;
    }

    openMega(id, trigger) {
      if (!id || !this.isDesktop()) return;
      const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
      const target = this.querySelector(`[data-wm-mega="${safeId}"]`);
      if (!target) return;
      if (this.activeMega === id) {
        this.cancelCloseMega();
        return;
      }

      this.cancelCloseMega();
      if (this._megaHideTimer) {
        window.clearTimeout(this._megaHideTimer);
        this._megaHideTimer = null;
      }

      const previousId = this.activeMega;
      const previous = previousId
        ? this.querySelector(
            `[data-wm-mega="${window.CSS && CSS.escape ? CSS.escape(previousId) : previousId}"]`
          )
        : null;
      const isSwitch = Boolean(previous && previous !== target);
      const reduceMotion = this.prefersReducedMotion();

      this.activeMega = id;
      this.classList.add('is-mega-open');
      this.megaViewport?.setAttribute('aria-hidden', 'false');

      this.querySelectorAll('[data-wm-mega]').forEach((menu) => {
        menu.removeAttribute('data-motion');
        menu.classList.toggle('is-open', menu === target);
      });

      if (isSwitch && !reduceMotion) {
        const prevIndex = this.getMegaIndex(previousId);
        const nextIndex = this.getMegaIndex(id);
        if (prevIndex > -1 && nextIndex > -1 && prevIndex !== nextIndex) {
          previous.classList.add('is-open');
          if (nextIndex > prevIndex) {
            previous.setAttribute('data-motion', 'to-start');
            target.setAttribute('data-motion', 'from-end');
          } else {
            previous.setAttribute('data-motion', 'to-end');
            target.setAttribute('data-motion', 'from-start');
          }
          window.setTimeout(() => {
            previous.classList.remove('is-open');
            previous.removeAttribute('data-motion');
          }, 20);
          window.setTimeout(() => {
            if (this.activeMega === id) target.removeAttribute('data-motion');
          }, 620);
        }
      }

      this.querySelectorAll('[data-wm-mega-trigger]').forEach((item) => {
        item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
      });

      void target.offsetHeight;
      this.syncMegaViewportHeight(target);
    }

    closeMega() {
      this.cancelCloseMega();
      if (!this.activeMega && !this.classList.contains('is-mega-open')) return;

      this.activeMega = null;
      this.classList.remove('is-mega-open');
      this.querySelectorAll('[data-wm-mega-trigger]').forEach((item) =>
        item.setAttribute('aria-expanded', 'false')
      );

      if (this.megaViewport) {
        this.megaViewport.style.height = '0px';
        this.megaViewport.setAttribute('aria-hidden', 'true');
      }

      const hideDelay = this.prefersReducedMotion() ? 0 : 350;
      if (this._megaHideTimer) window.clearTimeout(this._megaHideTimer);
      this._megaHideTimer = window.setTimeout(() => {
        this.querySelectorAll('[data-wm-mega]').forEach((menu) => {
          menu.classList.remove('is-open');
          menu.removeAttribute('data-motion');
        });
        this._megaHideTimer = null;
      }, hideDelay);
    }
  }

  if (!customElements.get('molecule-header')) {
    customElements.define('molecule-header', MoleculeHeader);
  }
})();
